/** Shared product and cart promise. The business clock is fixed UTC+2 year-round. */
const OFFSET_MS = 2 * 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;
const MARKERS = '[data-order-cutoff], [data-delivery-date]';

export const getDeliveryEstimate = (now = new Date(), locale = 'fr') => {
  const local = new Date(now.getTime() + OFFSET_MS);
  let cutoff =
    Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate(), 10) - OFFSET_MS;

  // At exactly 10:00:00, today's cutoff still applies.
  if (now.getTime() > cutoff) cutoff += 24 * 60 * MINUTE_MS;

  const remainingMinutes = Math.ceil((cutoff - now.getTime()) / MINUTE_MS);
  const hours = Math.floor(remainingMinutes / 60);
  const minutes = remainingMinutes % 60;
  const duration =
    [hours && `${hours}h`, minutes && `${minutes}min`].filter(Boolean).join(' ') || '0min';

  const deliveryDay = new Date(
    Date.UTC(local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate() + 3),
  );
  const date = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  }).format(deliveryDay);

  return { duration, date, showCountdown: remainingMinutes <= 12 * 60 };
};

export const initDeliveryEstimate = (root = document, now = new Date()) => {
  const cutoffs = root.querySelectorAll('[data-order-cutoff]');
  const dates = root.querySelectorAll('[data-delivery-date]');
  if (!cutoffs.length && !dates.length) return;

  const locale = document.documentElement.lang || 'fr';
  const { duration, date, showCountdown } = getDeliveryEstimate(now, locale);
  root.querySelectorAll('[data-order-countdown]').forEach((element) => {
    element.hidden = !showCountdown;
  });
  root.querySelectorAll('[data-order-now]').forEach((element) => {
    element.hidden = showCountdown;
  });
  cutoffs.forEach((element) => {
    if (element.textContent !== duration) element.textContent = duration;
  });
  dates.forEach((element) => {
    if (element.textContent !== date) element.textContent = date;
  });
};

if (typeof window !== 'undefined') {
  // Existing product/cart components call this after Shopify section rerenders.
  /** @type {Window & { LightindermDeliveryEstimate?: { init(root?: ParentNode): void } }} */
  const deliveryWindow = window;
  deliveryWindow.LightindermDeliveryEstimate = { init: initDeliveryEstimate };

  const start = () => {
    initDeliveryEstimate();

    const refreshAtNextMinute = () => {
      window.setTimeout(() => {
        initDeliveryEstimate();
        refreshAtNextMinute();
      }, MINUTE_MS - (Date.now() % MINUTE_MS) + 50);
    };
    refreshAtNextMinute();

    // Sections can also arrive without their inline scripts executing.
    new MutationObserver((mutations) => {
      if (
        mutations.some(({ addedNodes }) =>
          [...addedNodes].some(
            (node) =>
              node instanceof Element &&
              (node.matches(MARKERS) || node.querySelector(MARKERS)),
          ),
        )
      ) {
        initDeliveryEstimate();
      }
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) initDeliveryEstimate();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
}
